import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ChevronLeft, Building2, FileText, Gavel, Receipt, Sparkles,
  CalendarClock, IndianRupee, Lock, Unlock, Users, UserPlus, X, Eye,
} from "lucide-react";
import { prettify } from "./DeskDetail";

/* ClientDetail — timeline + revenue view for one client.
 *
 *  Route: /office/desks/client/:id
 *
 *  Tabs: Overview · Briefs · Bids · Invoices · Revenue (gated)
 */
export default function ClientDetail() {
  const { id } = useParams();
  const [timeline, setTimeline] = useState(null);
  const [rev, setRev]           = useState(null);
  const [revErr, setRevErr]     = useState(null);
  const [tab, setTab]           = useState("overview");
  const [staff, setStaff]       = useState([]);
  const [showAccessModal, setShowAccessModal] = useState(false);

  const load = useCallback(async () => {
    const [{ data: t }, { data: s }] = await Promise.all([
      api.get(`/agency/clients/${id}/timeline`),
      api.get("/staff/mine").catch(() => ({ data: { items: [] } })),
    ]);
    setTimeline(t);
    setStaff(s.items || []);
    // Try revenue — 403 gracefully caught
    try {
      const { data: r } = await api.get(`/agency/clients/${id}/revenue`);
      setRev(r); setRevErr(null);
    } catch (e) {
      setRev(null);
      setRevErr(e?.response?.data?.detail || "Restricted");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!timeline) {
    return <div className="mx-auto max-w-4xl px-6 py-16 text-center text-white/55">Loading client…</div>;
  }
  const client = timeline.client;
  const d      = client.data || {};

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8"
         data-testid="client-detail-page">
      <Link to="/office/desks/client"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-white/50 hover:text-white transition">
        <ChevronLeft size={12} /> Back to Client Roster
      </Link>

      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Client · {prettify(client.status)}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-1"
              data-testid="client-brand">
            {d.brand_name || client.title}
          </h1>
          <div className="mt-2 text-[11.5px] text-white/60 flex flex-wrap gap-x-4">
            {d.poc_name && <span>POC · {d.poc_name}</span>}
            {d.poc_email && <span>{d.poc_email}</span>}
            {d.poc_phone && <span>{d.poc_phone}</span>}
            {d.industry && <span className="text-white/45">{d.industry}</span>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-1 border-b border-white/10 overflow-x-auto"
           data-testid="client-detail-tabs">
        <TabBtn label="Overview"        n={null}                                  active={tab === "overview"}   on={() => setTab("overview")}   testid="client-tab-overview" />
        <TabBtn label="Briefs"          n={timeline.totals.brief}                 active={tab === "briefs"}     on={() => setTab("briefs")}     testid="client-tab-briefs" />
        <TabBtn label="Bids"            n={timeline.totals.bid}                   active={tab === "bids"}       on={() => setTab("bids")}       testid="client-tab-bids" />
        <TabBtn label="Invoices"        n={timeline.totals.invoice}               active={tab === "invoices"}   on={() => setTab("invoices")}   testid="client-tab-invoices" />
        <TabBtn label="Campaigns"       n={timeline.totals.campaign}              active={tab === "campaigns"}  on={() => setTab("campaigns")}  testid="client-tab-campaigns" />
        <TabBtn label="Revenue"         n={null} icon={rev ? Unlock : Lock}       active={tab === "revenue"}    on={() => setTab("revenue")}    testid="client-tab-revenue" />
      </div>

      <div className="mt-6">
        {tab === "overview" && <OverviewTab timeline={timeline} rev={rev} revErr={revErr} onOpenRevenue={() => setTab("revenue")} />}
        {tab === "briefs"     && <ItemGrid items={timeline.briefs}     kind="brief"     icon={FileText}     empty="No briefs linked yet." />}
        {tab === "bids"       && <ItemGrid items={timeline.bids}       kind="bid"       icon={Gavel}        empty="No bids linked yet." />}
        {tab === "invoices"   && <ItemGrid items={timeline.invoices}   kind="invoice"   icon={Receipt}      empty="No invoices linked yet." />}
        {tab === "campaigns"  && <ItemGrid items={timeline.campaigns}  kind="campaign"  icon={Sparkles}     empty="No campaigns linked yet." />}
        {tab === "revenue"    && (
          <RevenueTab rev={rev} revErr={revErr}
                      staff={staff}
                      canManageAccess={rev && (rev.viewer_reason === "owner" ||
                                               rev.viewer_reason?.startsWith("role"))}
                      onOpenAccess={() => setShowAccessModal(true)} />
        )}
      </div>

      {showAccessModal && (
        <AccessModal clientId={id} rev={rev} staff={staff}
                     onClose={() => setShowAccessModal(false)}
                     onChanged={load} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function TabBtn({ label, n, icon: Icon, active, on, testid }) {
  return (
    <button onClick={on}
      className={"px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase font-semibold border-b-2 transition inline-flex items-center gap-1.5 " +
        (active ? "border-white text-white" : "border-transparent text-white/50 hover:text-white/80")}
      data-testid={testid}>
      {Icon && <Icon size={12} />}
      {label}
      {n != null && <span className="text-white/40">· {n}</span>}
    </button>
  );
}

function OverviewTab({ timeline, rev, revErr, onOpenRevenue }) {
  const t = timeline.totals;
  const stats = [
    { key: "brief",    label: "Briefs",    n: t.brief    || 0, icon: FileText,    color: "#6EE7B7" },
    { key: "bid",      label: "Bids",      n: t.bid      || 0, icon: Gavel,       color: "#F59E0B" },
    { key: "invoice",  label: "Invoices",  n: t.invoice  || 0, icon: Receipt,     color: "#60A5FA" },
    { key: "campaign", label: "Campaigns", n: t.campaign || 0, icon: Sparkles,    color: "#F472B6" },
  ];
  return (
    <div className="space-y-6" data-testid="client-tab-overview-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const I = s.icon;
          return (
            <div key={s.key} className="cs-card rounded-xl p-5"
                 data-testid={`client-stat-${s.key}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                   style={{ background: `${s.color}22`, color: s.color }}>
                <I size={17} />
              </div>
              <div className="mt-3 font-display text-3xl tabular-nums">{s.n}</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="cs-card rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <IndianRupee size={14} className="text-white/60" />
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Revenue</div>
        </div>
        {rev ? (
          <div>
            <div className="font-display text-4xl tabular-nums">
              ₹{Number(rev.totals.total_billed).toLocaleString("en-IN")}
            </div>
            <div className="mt-1 text-[11.5px] text-white/50">
              Total billed · <button className="underline underline-offset-2 hover:text-white" onClick={onOpenRevenue}>see breakdown</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="font-display text-lg text-white/60">Restricted</div>
            <div className="mt-1 text-[11.5px] text-white/45">
              {revErr || "Only elevated roles (Founder / MD / COO / Finance / Chief Strategy) see revenue by default."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ItemGrid({ items, kind, icon: Icon, empty }) {
  if (!items || items.length === 0) {
    return (
      <div className="cs-card rounded-xl p-10 text-center text-sm text-white/50"
           data-testid={`client-empty-${kind}`}>
        <Icon size={22} className="mx-auto text-white/30 mb-2" />
        {empty}
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid={`client-list-${kind}`}>
      {items.map((it) => (
        <Link key={it.id}
          to={`/office/desks/${kind}`}
          className="cs-card rounded-xl p-4 hover:border-white/25 block"
          data-testid={`client-linked-${it.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base uppercase tracking-tight truncate">{it.title}</div>
            <span className="px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase font-semibold bg-white/8 text-white/70">
              {prettify(it.status)}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-white/55">
            {(it.data?.amount || it.data?.bid_amount) && (
              <span>₹{Number(it.data.amount || it.data.bid_amount).toLocaleString("en-IN")}</span>
            )}
            {it.data?.deadline && <span>Due {it.data.deadline}</span>}
            {it.data?.brief_summary && <span className="text-white/45 truncate max-w-md">{it.data.brief_summary}</span>}
          </div>
        </Link>
      ))}
    </div>
  );
}

function RevenueTab({ rev, revErr, staff, canManageAccess, onOpenAccess }) {
  if (!rev) {
    return (
      <div className="cs-card rounded-2xl p-10 text-center" data-testid="client-revenue-locked">
        <Lock size={28} className="mx-auto text-amber-300 mb-3" />
        <div className="font-display text-lg uppercase tracking-tight">Revenue is restricted</div>
        <p className="mt-2 text-sm text-white/55 max-w-lg mx-auto">
          {revErr || "By default only Founders / MDs / COOs / Finance / Chief Strategy Officers can view revenue. Ask them to grant you access for this client."}
        </p>
      </div>
    );
  }

  const t = rev.totals;
  const cards = [
    { key: "paid",         label: "Collected",    v: t.paid,         color: "#6EE7B7" },
    { key: "sent",         label: "Awaiting Payment", v: t.sent,     color: "#60A5FA" },
    { key: "overdue",      label: "Overdue",      v: t.overdue,      color: "#F87171" },
    { key: "pipeline",     label: "Pipeline (Bids)", v: t.pipeline_from_bids, color: "#F59E0B" },
    { key: "won",          label: "Won Bids",     v: t.won_from_bids, color: "#A78BFA" },
    { key: "total_billed", label: "Total Billed", v: t.total_billed, color: "#FFFFFF" },
  ];

  return (
    <div className="space-y-6" data-testid="client-revenue-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.key} className="cs-card rounded-xl p-5"
               data-testid={`client-revenue-${c.key}`}>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">{c.label}</div>
            <div className="mt-1 font-display text-3xl tabular-nums" style={{ color: c.color }}>
              ₹{Number(c.v || 0).toLocaleString("en-IN")}
            </div>
          </div>
        ))}
      </div>

      <div className="cs-card rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Who can see this revenue?
            </div>
            <div className="mt-1 font-display text-lg uppercase tracking-tight">
              {rev.revenue_viewers.length} extra viewer{rev.revenue_viewers.length === 1 ? "" : "s"}
            </div>
            <div className="mt-1 text-[11px] text-white/45 max-w-md">
              Founders / MDs / COOs / Finance / Chief Strategy always see revenue.
              Additional per-client grants below.
            </div>
          </div>
          {canManageAccess && (
            <button onClick={onOpenAccess}
              className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold border border-white/25 hover:bg-white hover:text-black transition inline-flex items-center gap-1.5"
              data-testid="client-access-manage">
              <UserPlus size={12} /> Manage Access
            </button>
          )}
        </div>
        {rev.revenue_viewers.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {rev.revenue_viewers.map((uid) => {
              const u = staff.find((s) => s.id === uid);
              return (
                <span key={uid}
                      className="px-2.5 py-1 rounded-full text-[11px] bg-emerald-500/15 text-emerald-200"
                      data-testid={`client-viewer-chip-${uid}`}>
                  {u?.name || uid}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AccessModal({ clientId, rev, staff, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const viewers = new Set(rev?.revenue_viewers || []);

  const toggle = async (uid, action) => {
    setBusy(true);
    try {
      await api.post(`/agency/clients/${clientId}/revenue-access`,
                     { user_id: uid, action });
      toast.success(action === "grant" ? "Access granted" : "Access revoked");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
         data-testid="client-access-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md cs-card rounded-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Revenue access
            </div>
            <div className="mt-0.5 font-display text-lg uppercase tracking-tight">
              Grant per-client access
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-white/8">
            <X size={16} />
          </button>
        </div>
        <p className="text-[11.5px] text-white/55 mb-4">
          Pick individuals who should see this client&apos;s revenue even if their
          role doesn&apos;t include it (e.g. Account Directors for their own clients).
        </p>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {staff.filter((s) => s.is_active !== false).map((s) => {
            const has = viewers.has(s.id);
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/6"
                   data-testid={`client-access-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] text-white/90 truncate">{s.name}</div>
                  <div className="text-[10.5px] text-white/45 truncate">
                    {s.agency_role_label || "—"}
                  </div>
                </div>
                <button onClick={() => toggle(s.id, has ? "revoke" : "grant")}
                  disabled={busy}
                  className={"h-7 px-3 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold transition disabled:opacity-40 " +
                    (has ? "bg-emerald-500/15 text-emerald-200 hover:bg-red-500 hover:text-black"
                         : "border border-white/25 text-white/70 hover:bg-white hover:text-black")}
                  data-testid={`client-access-toggle-${s.id}`}>
                  {has ? "Revoke" : "Grant"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
